import {
 createTheme,
 Shadows,
 Theme,
 ThemeOptions,
} from '@mui/material/styles';
// The import can be in any file that is included in your `tsconfig.json`
import type {} from '@mui/material/themeCssVarsAugmentation';
import type {} from '@mui/x-data-grid-premium/themeAugmentation'; // important
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import IconAngleBottom from '@assets/icons/angle-bottom.svg?react';
import { Box } from '@mui/material';
import customColors from './customColors';
import customColorsDark from './customColorsDark';
import IconCheck from '@assets/icons/check.svg?react';
import IconCheckbox from '@assets/icons/checkbox.svg?react';
import IconCalendar from '@assets/icons/calendar.svg?react';

declare module '@mui/material/styles' {
 interface PaletteOptions {
  kemek: {
   black: string;
   darkestGray: string;
   darkGray: string;
   gray: string;
   lightGray: string;
   lightestGray: string;
   white: string;
   blue: string;
   darkBlue: string;
   lightBlue: string;
   green: string;
   lightGreen: string;
   red: string;
   lightRed: string;
   lightestRed: string;
   orange: string;
   lightOrange: string;
   yellow: string;
   lightYellow: string;
   bg1: string;
   bg2: string;
  };
 }
 interface Palette {
  kemek: {
   black: string;
   darkestGray: string;
   darkGray: string;
   gray: string;
   lightGray: string;
   lightestGray: string;
   white: string;
   blue: string;
   darkBlue: string;
   lightBlue: string;
   green: string;
   lightGreen: string;
   red: string;
   lightRed: string;
   lightestRed: string;
   orange: string;
   lightOrange: string;
   yellow: string;
   lightYellow: string;
   bg1: string;
   bg2: string;
  };
 }
}

// create a temporary theme to get the default options
const defaultTheme = createTheme();

const defaultShadows: ThemeOptions['shadows'] = [...defaultTheme.shadows];

const themeOptions = () => {
 let th = createTheme({
  shadows: defaultShadows.map(
   () => '0px 4px 12px 0px rgba(0, 0, 0, 0.08)',
  ) as Shadows,
  colorSchemes: {
   light: {
    palette: {
     kemek: customColors,
     primary: {
      main: customColors.blue,
     },
     secondary: {
      main: customColors.darkGray,
     },
     background: {
      default: customColors.bg2,
      paper: '#ffffff',
     },
     text: {
      primary: customColors.darkestGray,
      secondary: customColors.darkestGray,
      disabled: customColors.gray,
     },
     error: {
      main: customColors.red,
      contrastText: '#ffffff',
     },
     warning: {
      main: '#f3ca36',
      contrastText: '#ffffff',
     },
     info: {
      main: customColors.blue,
      contrastText: '#ffffff',
     },
     success: {
      main: customColors.green,
      contrastText: '#ffffff',
     },

     // amber: {
     //  lev0: amber[50],
     //  lev1: amber[100],
     //  lev2: amber[200],
     //  lev3: amber[300],
     //  lev4: amber[400],
     //  lev5: amber[500],
     //  lev6: amber[600],
     //  lev7: amber[700],
     //  lev8: amber[800],
     //  lev9: amber[900],
     // },
    },
   },
   dark: {
    palette: {
     kemek: customColorsDark,
     primary: {
      main: customColorsDark.blue,
     },
     secondary: {
      main: customColorsDark.darkGray,
     },
     text: {
      primary: customColorsDark.darkestGray,
      secondary: customColorsDark.darkestGray,
      disabled: customColorsDark.gray,
     },
     background: {
      default: customColorsDark.bg2,
      paper: customColorsDark.white,
     },
    },
   },
  },
  defaultColorScheme: 'light',
  typography: {
   fontFamily: 'Poppins',
   fontSize: 12.25, // fix
   button: {
    textTransform: 'none',
   },
  },
  shape: {
   borderRadius: 4,
  },
 });
 const PickerOpenIcon = () => (
  <IconCalendar style={{ color: th.palette.kemek.gray }} />
 );
 th = createTheme(th, {
  cssVariables: true,

  components: {
   MuiDatePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiDesktopDatePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiMobileDatePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiDateTimePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiDesktopDateTimePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiMobileDateTimePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiDateRangePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiDesktopDateRangePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiMobileDateRangePicker: {
    defaultProps: {
     slots: {
      openPickerIcon: PickerOpenIcon,
     },
    },
   },
   MuiDateRangeCalendar: {
    styleOverrides: {
     root: {
      '& .MuiDateRangePickerDay-day': {
       width: '30px',
       height: '30px',
       transform: 'none',
       '& > *': {
        transform: 'none',
       },
      },
      '& .MuiPickersDay-root': {
       width: '30px',
       height: '30px',
      },
      '& .MuiDayCalendar-weekDayLabel': {
       width: '30px',
       height: '30px',
       margin: '0 1.8px',
      },
      '& .MuiDayCalendar-weekContainer': {
       margin: '1.8px 0',
      },
      '& .MuiDayCalendar-root': {
       minWidth: '248px',
       minHeight: '214px',
      },
      '& .MuiDayCalendar-slideTransition': {
       minHeight: '218px',
       minWidth: '252px',
      },
      '& .MuiDayCalendar-loadingContainer': {
       minWidth: 252,
       minHeight: 217,
      },
      '& .MuiPickersCalendarHeader-root': {
       marginTop: '11px',
       marginBottom: '4px',
       paddingLeft: '21px',
       paddingRight: '11px',
       maxHeight: '36px',
       minHeight: '36px',
      },
      '& .MuiPickersArrowSwitcher-root': {
       padding: '11px 14px 4px 14px',
      },
     },
    },
   },
   MuiDataGrid: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      border: theme.palette.mode === 'dark' ? 'none' : '#e0e0e0',
     }),
     menu: ({ theme }: { theme: Theme }) => ({
      '& .MuiDataGrid-menuList': {
       padding: 0,
       border: `1px solid ${theme.palette.kemek.lightestGray}`,
       borderRadius: 4,
       '& .MuiDivider-root': {
        display: 'none !important',
       },
      },
     }),
    },
   },
   MuiInputBase: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({}),
    },
   },
   MuiOutlinedInput: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => {
      return {
       paddingTop: '12px',
       paddingBottom: '12px',
       '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.kemek.lightGray, // default
        transition: 'border-color 0.3s ease-in-out',
       },
       '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.kemek.gray, // hover
       },
       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.kemek.darkGray, // focus
       },
       '& .MuiOutlinedInput-root:has(input:not(:placeholder-shown)) .MuiOutlinedInput-notchedOutline':
        {
         borderColor: 'purple',
        },
       '& .MuiOutlinedInput-input.Mui-disabled': {
        color: theme.palette.kemek.lightGray,

        '& .MuiOutlinedInput-notchedOutline': {
         borderColor: theme.palette.kemek.lightGray,
        },
       },
       '&:not(.MuiInputBase-multiline)': {
        '& .MuiOutlinedInput-input': {
         paddingTop: '0px',
         paddingBottom: '0px',
         height: '24px',
         '&.MuiSelect-select': {
          //  height: 'auto',
         },
        },
        '&.MuiInputBase-sizeSmall': {
         paddingTop: '8px',
         paddingBottom: '8px',
         minHeight: '40px',
         '& .MuiOutlinedInput-input': {
          paddingTop: '0px',
          paddingBottom: '0px',
          '&.MuiSelect-select': {
           // height: '24px',
          },
         },
        },
       },
       '&.Mui-disabled': {
        '&:hover .MuiOutlinedInput-notchedOutline': {
         borderColor: theme.palette.kemek.lightGray, // hover
        },
       },
      };
     },
    },
   },
   MuiInputLabel: {
    styleOverrides: {
     outlined: {
      transform: 'translate(14px, 14px) scale(1)',
      '&.MuiInputLabel-shrink': {
       transform: 'translate(14px, -8px) scale(0.75)',
      },
      '&.MuiInputLabel-sizeSmall': {
       transform: 'translate(14px, 10px) scale(1)',
       '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -8px) scale(0.75)',
       },
      },
     },
     filled: {
      transform: 'translate(14px, 17px) scale(1)',
      '&.MuiInputLabel-shrink': {
       transform: 'translate(12px, 3px) scale(0.75)',
      },
      '&.MuiInputLabel-sizeSmall': {
       transform: 'translate(14px, 10px) scale(1)',
       '&.MuiInputLabel-shrink': {
        transform: 'translate(12px, 3px) scale(0.75)',
       },
      },
     },
     root: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.kemek.darkGray,
      '&.Mui-focused': {
       color: theme.palette.kemek.darkestGray,
      },
     }),
    },
   },
   MuiFormControlLabel: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      marginLeft: 0,
     }),
    },
   },

   MuiSelect: {
    defaultProps: {
     IconComponent: () => (
      <Box
       sx={(theme) => ({
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // marginRight: '12px',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        right: '9px',
        pointerEvents: 'none',
        color: theme.palette.kemek.darkestGray,
       })}
      >
       <IconAngleBottom style={{}} />
      </Box>
     ),
    },
    styleOverrides: {
     select: {
      '&.MuiOutlinedInput-input.MuiInputBase-inputSizeSmall': {
       height: '20px',
      },
     },
    },
   },
   MuiAutocomplete: {
    defaultProps: {
     popupIcon: (
      <Box
       sx={(theme) => ({
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.kemek.darkestGray,
        pointerEvents: 'none',
       })}
      >
       <IconAngleBottom style={{}} />
      </Box>
     ),
    },
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      '& .MuiOutlinedInput-root': {
       paddingTop: '8px',
       paddingBottom: '8px',
       minHeight: '48px',
       '& .MuiAutocomplete-input': {
        padding: 0,
        paddingLeft: '6px',
       },
       '&.MuiInputBase-sizeSmall': {
        paddingTop: '8px',
        paddingBottom: '8px',
        minHeight: '40px',
       },

       '& .MuiAutocomplete-tag': {
        margin: '2px',
        height: '24px',
       },
       '&.Mui-disabled': {
        '& .MuiBox-root': {
         color: theme.palette.kemek.lightGray, // angle down svg color
        },
       },
      },
      // this is a bad hack, fix it
      '&.MuiAutocomplete-hasClearIcon': {
       '& .MuiOutlinedInput-root': {
        paddingTop: '10px',
        paddingBottom: '10px',
        '&.MuiInputBase-sizeSmall': {
         paddingTop: '6px',
         paddingBottom: '6px',
        },
       },
      },
      // '& .MuiOutlinedInput-input': {
      //  height: '24px',
      //  padding: 0,
      // },
      // '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {},
     }),
    },
   },
   MuiCssBaseline: {
    styleOverrides: (theme: Theme) => {
     return {
      body: {
       '[data-mui-color-scheme="dark"]': {
        '--Paper-overlay': 'none',
       },
       '& ::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
       },
       '& ::-webkit-scrollbar-track': {
        background: theme.palette.kemek.lightestGray,
       },
       '& ::-webkit-scrollbar-thumb': {
        background: theme.palette.kemek.gray,
        borderRadius: '3px',
        scrollbarColor:
         theme.palette.mode === 'dark' ? '#32353A #969696' : '#969696 #dddddd',
       },
       // column menu
       '& .MuiDataGrid-menuList': {
        padding: 0,
        border: `1px solid ${theme.palette.kemek.lightestGray}`,
        borderRadius: 4,
        '& .MuiDivider-root': {
         display: 'none !important',
        },
       },
      },
     };
    },
   },
   MuiIconButton: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      padding: '4px',
      marginRight: 0,
      '&.Mui-disabled': {
       color: theme.palette.kemek.gray,
      },
     }),
    },
   },
   MuiButton: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      fontSize: '12px', // fix
      lineHeight: 1.5,
      '&.MuiButton-outlined': {
       height: '40px',
       '&.MuiButton-colorPrimary': {
        borderColor: theme.palette.kemek.blue,
        '&:hover': {
         background: theme.palette.kemek.lightBlue,
        },
        '&:active': {
         background: theme.palette.kemek.bg1,
        },
       },
       '&.MuiButton-colorSecondary': {
        borderColor: theme.palette.kemek.lightGray,
        color: theme.palette.kemek.darkGray,
        '&:hover': {
         background: theme.palette.mode === 'dark' ? '#121418' : 'transparent',
        },
        '&:active': {
         background: theme.palette.mode === 'dark' ? '#15181D' : 'transparent',
        },
        '&.Mui-disabled': {
         //  borderColor: theme.palette.kemek.lightGray,
         color: theme.palette.kemek.lightGray,
        },
       },
       '&.MuiButton-colorError': {
        '&:hover': {
         background:
          theme.palette.mode === 'dark'
           ? '#260E0E'
           : theme.palette.kemek.lightestRed,
        },
        '&:active': {
         background:
          theme.palette.mode === 'dark'
           ? '#3F1818'
           : theme.palette.kemek.lightRed,
        },
       },
      },
      '&.MuiButton-contained': {
       height: '40px',
       '&.MuiButton-colorPrimary': {
        '&:hover': {
         background: theme.palette.kemek.darkBlue,
        },
        '&:active': {
         background: theme.palette.kemek.lightBlue,
        },
       },
       '&.MuiButton-colorSecondary': {
        '&:hover': {
         background: theme.palette.kemek.bg2,
        },
        '&:active': {
         background: theme.palette.kemek.bg1,
        },
       },
      },
      '&.Mui-disabled': {
       '&.MuiButton-outlined': {
        borderColor: theme.palette.kemek.lightGray,
        color: theme.palette.kemek.lightGray,
       },
       '&.MuiButton-contained': {
        color: theme.palette.kemek.white,
        backgroundColor: theme.palette.kemek.lightGray,
        // backgroundColor: th.palette.kemek.white,
       },
      },
     }),
    },
   },
   MuiCheckbox: {
    defaultProps: {
     icon: (
      <Box
       sx={{
        width: '16px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
       }}
      >
       <IconCheckbox style={{ width: '16px', height: '16px' }} />
      </Box>
     ),
     checkedIcon: (
      <Box
       sx={{
        width: '16px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        borderRadius: '2px',
        backgroundColor: (theme: Theme) => theme.palette.kemek.blue,
       }}
      >
       <IconCheck
        style={{ color: th.palette.kemek.white, width: '8px', height: '6px' }}
       />
      </Box>
     ),
     indeterminateIcon: (
      <Box
       sx={{
        width: '16px',
        height: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        borderRadius: '2px',
        backgroundColor: (theme: Theme) => theme.palette.kemek.blue,
       }}
      >
       <Box
        sx={{
         width: '8px',
         height: '2px',
         borderRadius: '1px',
         backgroundColor: (theme: Theme) => theme.palette.kemek.white,
        }}
       />
      </Box>
     ),
    },
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.kemek.gray,
     }),
    },
   },
   MuiAppBar: {
    styleOverrides: {
     colorPrimary: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.kemek.darkestGray,
     }),
    },
   },
   MuiPaper: {
    styleOverrides: {
     root: {
      '--Paper-overlay': 'none',
      backgroundImage: 'none',
     },
    },
   },
   MuiAccordion: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      backgroundImage: 'none',
     }),
    },
   },
   MuiDialog: {
    styleOverrides: {
     paper: ({ theme }: { theme: Theme }) => ({
      backgroundImage: 'none', // removes overlay that is visible in dark mode
     }),
    },
   },
   MuiDialogContent: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      padding: '16px 24px 0 24px',
     }),
    },
   },
   MuiDialogActions: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      padding: '24px',
      '&>:not(style)~not(style)': {
       //  background: '5px solid red',
      },
     }),
    },
   },
   MuiDivider: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      borderColor: theme.palette.kemek.lightestGray,
     }),
    },
   },
   MuiCard: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      boxShadow: 'none',
     }),
    },
   },
   MuiCardContent: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      backgroundColor:
       theme.palette.mode === 'dark'
        ? theme.palette.kemek.bg1
        : theme.palette.kemek.white,
      '&:last-child': {
       paddingBottom: '16px',
      },
     }),
    },
   },
   MuiMenu: {
    styleOverrides: {
     paper: ({ theme }: { theme: Theme }) => ({
      border: `1px solid ${theme.palette.kemek.lightestGray}`,
     }),
    },
   },
   MuiMenuItem: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      fontSize: 12,
      gap: '8px',
      padding: '8px 20px 8px 8px',
      borderBottom: `1px solid ${theme.palette.kemek.lightestGray}`,
      '&.Mui-selected': {
       backgroundColor: theme.palette.kemek.lightBlue,
      },
      '&:last-child': {
       borderBottom: 'none',
      },
      '&:hover': {
       backgroundColor:
        theme.palette.mode === 'dark'
         ? '#232934'
         : theme.palette.kemek.lightBlue,
      },
     }),
    },
   },
   MuiListItemIcon: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      minWidth: '24px !important',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      // paddingLeft: '4px !important',
      justifyContent: 'center',
     }),
    },
   },
   MuiTabs: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      overflow: 'visible',
     }),
     scroller: {
      overflow: 'visible !important',
     },
    },
   },
   MuiTab: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      fontWeight: 400,
     }),
    },
   },
   //  MuiFilledInput: {
   //   styleOverrides: {
   //    root: ({ theme }: { theme: Theme }) => ({
   //     background: theme.palette.kemek.white,
   //     borderRadius: 4,
   //     border: `1px solid ${theme.palette.kemek.lightGray}`,
   //     '&.Mui-error': {
   //      border: `1px solid ${theme.palette.kemek.red}`,
   //     },
   //     '&:hover': {
   //      background: theme.palette.kemek.white,
   //     },
   //     '&.Mui-focused': {
   //      background: theme.palette.kemek.white,
   //     },
   //     '&:not(.MuiInputBase-multiline)': {
   //      '& .MuiFilledInput-input': {
   //       paddingTop: '0px',
   //       paddingBottom: '0px',
   //      },
   //      '&.MuiInputBase-sizeSmall .MuiFilledInput-input': {
   //       paddingTop: '0px',
   //       paddingBottom: '0px',
   //      },
   //     },
   //    }),
   //   },
   //  },
   MuiTextField: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      //  background: 'transparent',
     }),
    },
   },
   MuiListItem: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      '& .MuiTouchRipple-root': {
       display: 'none',
      },
     }),
    },
   },
   MuiTooltip: {
    styleOverrides: {
     tooltip: ({ theme }: { theme: Theme }) => ({
      backgroundColor: theme.palette.kemek.white,
      color: theme.palette.kemek.darkestGray,
      borderRadius: '4px',
      padding: '8px 12px',
      border: `1px solid ${theme.palette.kemek.lightestGray}`,
      boxShadow: `0 4px 12px 0 rgba(0, 0, 0, 0.08)`,
      fontSize: '12px',
     }),
     arrow: ({ theme }: { theme: Theme }) => ({
      color: theme.palette.kemek.white,
      '&::before': {
       border: `1px solid ${theme.palette.kemek.lightestGray}`,
       backgroundColor: theme.palette.kemek.white,
      },
     }),
    },
   },
   //  MuiBadge: {
   //   styleOverrides: {
   //    root: ({ theme }: { theme: Theme }) => ({
   //     // because of grid toolbar badge
   //     display: 'block',
   //     flexShrink: 'initial',
   //    }),
   //   },
   //  },
   MuiSwitch: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      '& .MuiSwitch-switchBase': {
       color: theme.palette.kemek.lightGray,
       '&.Mui-checked': {
        color: theme.palette.kemek.blue,
        '&+.MuiSwitch-track': {
         backgroundColor: theme.palette.kemek.lightBlue,
         opacity: 1,
        },
       },
      },
      '& .MuiSwitch-track': {
       backgroundColor: theme.palette.kemek.lightestGray,
       opacity: 1,
      },
     }),
    },
   },
   MuiMobileStepper: {
    styleOverrides: {
     root: ({ theme }: { theme: Theme }) => ({
      '& .MuiMobileStepper-dotActive': {
       backgroundColor: theme.palette.kemek.blue,
      },
     }),
     dots: ({ theme }: { theme: Theme }) => ({
      gap: '8px',
     }),
     dot: ({ theme }: { theme: Theme }) => ({
      margin: '0',
      backgroundColor: theme.palette.kemek.lightestGray,
     }),
    },
   },

   // MuiDialogTitle: {
   //  styleOverrides: {
   //   root: {
   //    fontSize: 18,
   //    fontWeight: 500,
   //   },
   //  },
   // },
  },
 });
 return th;
};
export default themeOptions;

